import { useState } from "react";
import { useTranslation } from "react-i18next";
import ScrollReveal from "@/components/feature/ScrollReveal";

type Tone = "primary" | "accent";
type ViewKey = "intl" | "national";

interface Place {
  id: string;
  badge: string;
  countryKey: string;
  cityKey: string;
  top: string;
  left: string;
}

interface TeamCard {
  id: string;
  imageSrc: string;
  titleKey: string;
  subKey: string;
  icon: string;
}

const internationalPlaces: Place[] = [
  { id: "us", badge: "US", countryKey: "location.country.us", cityKey: "location.city.miami", top: "18%", left: "80%" },
  { id: "co", badge: "CO", countryKey: "location.country.co", cityKey: "location.city.bogota", top: "45%", left: "66%" },
  { id: "ec", badge: "EC", countryKey: "location.country.ec", cityKey: "location.city.quito", top: "55%", left: "60%" },
  { id: "pe", badge: "PE", countryKey: "location.country.pe", cityKey: "location.city.lima", top: "68%", left: "65%" },
  { id: "cl", badge: "CL", countryKey: "location.country.cl", cityKey: "location.city.santiago", top: "82%", left: "68%" },
];

const nationalPlaces: Place[] = [
  { id: "lima", badge: "LI", countryKey: "location.country.pe", cityKey: "location.city.lima", top: "45%", left: "42%" },
  { id: "arequipa", badge: "AR", countryKey: "location.country.pe", cityKey: "location.city.arequipa", top: "68%", left: "55%" },
  { id: "cusco", badge: "CU", countryKey: "location.country.pe", cityKey: "location.city.cusco", top: "55%", left: "64%" },
];

const teamCards: TeamCard[] = [
  {
    id: "soc",
    icon: "ri-team-line",
    titleKey: "location.people.card1Title",
    subKey: "location.people.card1Sub",
    imageSrc:
      "https://readdy.ai/api/search-image?query=Candid%20documentary%20photo%20of%20a%20smiling%20cybersecurity%20analyst%20wearing%20a%20headset%20in%20front%20of%20multiple%20monitoring%20screens%20in%20a%20modern%20security%20operations%20center%2C%20warm%20friendly%20expression%2C%20real%20workplace%20atmosphere%2C%20natural%20soft%20lighting%20with%20subtle%20cyan%20screen%20glow%2C%20dark%20navy%20office%2C%20editorial%20photography&width=600&height=440&seq=location-team-01&orientation=landscape",
  },
  {
    id: "field",
    icon: "ri-customer-service-2-line",
    titleKey: "location.people.card2Title",
    subKey: "location.people.card2Sub",
    imageSrc:
      "https://readdy.ai/api/search-image?query=Candid%20photo%20of%20an%20IT%20support%20engineer%20with%20a%20laptop%20at%20a%20client%20site%20working%20alongside%20a%20colleague%2C%20friendly%20confident%20expression%2C%20genuine%20human%20moment%2C%20natural%20warm%20daylight%2C%20documentary%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20professional%20environment&width=600&height=440&seq=location-team-02&orientation=landscape",
  },
];

const toneStyles: Record<
  Tone,
  {
    bubble: string;
    dot: string;
    glow: string;
    ping: string;
    chipDot: string;
    tabActive: string;
  }
> = {
  primary: {
    bubble: "bg-gradient-to-br from-primary-400 to-primary-600 text-white",
    dot: "bg-gradient-to-br from-primary-200 to-primary-500",
    glow: "bg-primary-400/50",
    ping: "border-primary-300/50",
    chipDot: "bg-primary-300",
    tabActive: "bg-primary-500 text-white",
  },
  accent: {
    bubble: "bg-gradient-to-br from-accent-400 to-accent-600 text-white",
    dot: "bg-gradient-to-br from-accent-200 to-accent-500",
    glow: "bg-accent-400/50",
    ping: "border-accent-300/50",
    chipDot: "bg-accent-300",
    tabActive: "bg-accent-500 text-white",
  },
};

interface ViewConfig {
  key: ViewKey;
  titleKey: string;
  descKey: string;
  imageSrc: string;
  imageAltKey: string;
  places: Place[];
  tone: Tone;
  tabIcon: string;
  mapIcon: string;
  countKey: string;
}

const views: ViewConfig[] = [
  {
    key: "intl",
    titleKey: "location.intlTitle",
    descKey: "location.intlDesc",
    imageSrc:
      "https://readdy.ai/api/search-image?query=Elegant%20minimalist%20map%20of%20the%20Americas%20on%20a%20deep%20midnight%20navy%20background%2C%20glowing%20electric%20cyan%20neon%20country%20outlines%2C%20subtle%20dotted%20mesh%20network%20arcs%20linking%20major%20cities%2C%20soft%20particle%20glows%20at%20capitals%2C%20high%20contrast%20cartography%2C%20no%20text%20labels%2C%20refined%20corporate%20cybersecurity%20aesthetic%2C%20smooth%20gradient%20vignette%2C%20panoramic%20composition&width=1600&height=800&seq=nuvvens-americas-map-v2&orientation=landscape&nocache=true",
    imageAltKey: "location.intlTitle",
    places: internationalPlaces,
    tone: "primary",
    tabIcon: "ri-earth-line",
    mapIcon: "ri-map-2-line",
    countKey: "location.countryLabel",
  },
  {
    key: "national",
    titleKey: "location.nationalTitle",
    descKey: "location.nationalDesc",
    imageSrc:
      "https://readdy.ai/api/search-image?query=Elegant%20minimalist%20map%20of%20Peru%20on%20a%20deep%20midnight%20navy%20background%2C%20glowing%20warm%20amber%20neon%20department%20outlines%2C%20subtle%20dotted%20mesh%20network%20arcs%20linking%20Lima%20Arequipa%20and%20Cusco%2C%20soft%20particle%20glows%2C%20Andes%20and%20Amazon%20subtle%20shading%2C%20high%20contrast%20cartography%2C%20no%20text%20labels%2C%20refined%20corporate%20aesthetic%2C%20smooth%20gradient%20vignette%2C%20panoramic%20composition&width=1600&height=800&seq=nuvvens-peru-map-v2&orientation=landscape&nocache=true",
    imageAltKey: "location.nationalTitle",
    places: nationalPlaces,
    tone: "accent",
    tabIcon: "ri-map-pin-2-line",
    mapIcon: "ri-road-map-line",
    countKey: "location.siteLabel",
  },
];

function TeamCardItem({ card, className = "" }: { card: TeamCard; className?: string }) {
  const { t } = useTranslation("common");
  return (
    <div
      className={`group relative flex items-center gap-3 overflow-hidden rounded-xl border border-white/12 bg-[#0B1628]/85 backdrop-blur-md px-2.5 py-2 transition-colors duration-300 hover:border-white/25 ${className}`}
    >
      <div className="relative w-11 h-11 rounded-lg overflow-hidden flex-shrink-0">
        <img
          src={card.imageSrc}
          alt={t(card.titleKey)}
          width={600}
          height={440}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1628]/70 to-transparent" />
        <div className="absolute bottom-1 left-1 w-5 h-5 flex items-center justify-center rounded-md bg-primary-500/90 text-white">
          <i className={`${card.icon} text-[10px]`} />
        </div>
      </div>
      <div className="min-w-0">
        <p className="font-heading text-[11px] font-semibold text-foreground-50 truncate leading-tight">
          {t(card.titleKey)}
        </p>
        <p className="font-label text-[9px] text-foreground-400/70 truncate mt-0.5">{t(card.subKey)}</p>
      </div>
    </div>
  );
}

export default function LocationMap() {
  const { t } = useTranslation("common");
  const [view, setView] = useState<ViewKey>("intl");
  const [activeId, setActiveId] = useState<string | null>(null);

  const activeView = views.find((v) => v.key === view) as ViewConfig;
  const s = toneStyles[activeView.tone];

  const handleView = (key: ViewKey) => {
    setView(key);
    setActiveId(null);
  };

  return (
    <section id="ubicacion" className="relative py-14 md:py-20 bg-[#0B1628] overflow-hidden">
      {/* Human background image to bring warmth to the section (softened on small screens for legibility) */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Diverse%20team%20of%20cybersecurity%20professionals%20collaborating%20in%20a%20modern%20operations%20room%2C%20warm%20ambient%20lighting%2C%20genuine%20friendly%20atmosphere%2C%20engaged%20expressions%2C%20editorial%20photography%2C%20human%20centered%2C%20dark%20navy%20tones%20with%20subtle%20cyan%20highlights%2C%20cinematic%20depth%20of%20field&width=900&height=506&seq=location-human-bg-01&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center opacity-[0.06] md:opacity-[0.14]"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1628]/95 via-[#0B1628]/90 to-[#0B1628]" />
      </div>

      {/* Tech grid background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--primary-500) / 0.5) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.5) 1px, transparent 1px)",
          backgroundSize: "84px 84px",
        }}
      />
      {/* Radial glows */}
      <div className="absolute top-0 right-0 w-[620px] h-[340px] bg-primary-500/6 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[560px] h-[320px] bg-accent-500/6 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary-400/30 to-transparent" />

      <div className="relative z-10 w-full">
        {/* Header */}
        <ScrollReveal>
          <div className="px-4 sm:px-6 lg:px-10 pb-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-400/20 bg-accent-500/8 mb-4">
                  <i className="ri-global-line text-accent-300 text-xs" />
                  <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.22em]">
                    {t("location.tag")}
                  </span>
                </div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tight">
                  {t("location.title")}{" "}
                  <span className="text-accent-400">{t("location.highlight")}</span>
                </h2>
              </div>

              {/* Controls */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="hidden md:inline font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground-400/70">
                  {t("location.exploreLabel")}
                </span>
                <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1">
                  {views.map((v) => {
                    const isActive = view === v.key;
                    return (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => handleView(v.key)}
                        className={`inline-flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-full font-label text-xs font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 ${
                          isActive
                            ? toneStyles[v.tone].tabActive
                            : "text-foreground-300 hover:text-white hover:bg-white/[0.06]"
                        }`}
                      >
                        <i className={`${v.tabIcon} text-sm`} />
                        {t(v.titleKey)}
                      </button>
                    );
                  })}
                </div>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                  <i
                    className={`ri-map-pin-2-fill text-xs ${
                      activeView.tone === "primary" ? "text-primary-300" : "text-accent-300"
                    }`}
                  />
                  <span className="font-label text-[11px] font-semibold text-foreground-200">
                    {activeView.places.length} {t(activeView.countKey)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Map — full width like the "Threat Map" section */}
        <ScrollReveal delay={100}>
          <div className="w-full px-4 sm:px-6 lg:px-10">
            <div
              key={view}
              className="relative w-full rounded-xl overflow-hidden border border-white/10 aspect-[4/3] sm:aspect-[16/9] lg:aspect-[2/1] 2xl:aspect-[21/9] max-h-[760px]"
            >
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary-400/30 z-30 pointer-events-none" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary-400/30 z-30 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary-400/30 z-30 pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary-400/30 z-30 pointer-events-none" />

              {/* Map image */}
              <img
                src={activeView.imageSrc}
                alt={t(activeView.imageAltKey)}
                className="absolute inset-0 w-full h-full object-cover animate-map-bg-fade"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1628]/70 via-transparent to-[#0B1628]/25 pointer-events-none z-[5]" />
              {/* Fine grid over the map for a refined, instrumented look */}
              <div
                className="absolute inset-0 pointer-events-none z-[5] opacity-[0.04]"
                style={{
                  backgroundImage:
                    "linear-gradient(oklch(var(--primary-300) / 0.6) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-300) / 0.6) 1px, transparent 1px)",
                  backgroundSize: "56px 56px",
                }}
              />

              {/* Region label overlay */}
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0B1628]/72 backdrop-blur-md px-3 sm:px-4 py-2 sm:py-2.5 animate-fade-in">
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${s.bubble}`}>
                  <i className={`${activeView.mapIcon} text-base`} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading font-bold text-sm text-foreground-50 truncate tracking-tight">
                    {t(activeView.titleKey)}
                  </h3>
                  <p className="font-label text-[10px] text-foreground-400/70 truncate">{t(activeView.descKey)}</p>
                </div>
              </div>

              {/* Pins */}
              {activeView.places.map((place, i) => {
                const isActive = activeId === place.id;
                return (
                  <div
                    key={place.id}
                    className="absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                    style={{ top: place.top, left: place.left }}
                    onMouseEnter={() => setActiveId(place.id)}
                    onMouseLeave={() => setActiveId(null)}
                  >
                    <div
                      className="relative flex items-center justify-center animate-pin-cascade"
                      style={{ animationDelay: `${i * 90}ms` }}
                    >
                      <span className={`absolute w-6 h-6 rounded-full blur-[6px] ${s.glow}`} />
                      <span className={`absolute w-7 h-7 rounded-full border animate-ping ${s.ping}`} />
                      <span
                        className={`relative w-3.5 h-3.5 rounded-full ring-2 ring-white/30 transition-transform duration-300 ${s.dot} ${
                          isActive ? "scale-150" : ""
                        }`}
                      />
                      <span
                        className={`absolute left-1/2 bottom-full mb-3 -translate-x-1/2 whitespace-nowrap rounded-xl border border-white/12 bg-[#0B1628]/92 px-3 py-1.5 backdrop-blur-md transition-all duration-300 ${
                          isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
                        }`}
                      >
                        <span className="block font-heading text-[11px] font-semibold text-foreground-50">
                          {t(place.cityKey)}
                        </span>
                        <span className="block font-label text-[10px] text-foreground-400/70">
                          {t(place.countryKey)}
                        </span>
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Human team cards — compact, floating in the bottom corners on large screens */}
              <div className="hidden lg:block absolute bottom-3 left-3 z-20 w-52">
                <TeamCardItem card={teamCards[0]} />
              </div>
              <div className="hidden lg:block absolute bottom-3 right-3 z-20 w-52">
                <TeamCardItem card={teamCards[1]} />
              </div>

              {/* Edge fades into section background */}
              <div className="absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#0B1628] to-transparent z-[15] pointer-events-none" />
              <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#0B1628] to-transparent z-[15] pointer-events-none" />
              <div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-[#0B1628] to-transparent z-[15] pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#0B1628] to-transparent z-[15] pointer-events-none" />
            </div>

            {/* Human team cards — compact row on smaller screens */}
            <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
              {teamCards.map((card) => (
                <TeamCardItem key={card.id} card={card} />
              ))}
            </div>

            {/* City chips */}
            <div key={`chips-${view}`} className="flex flex-wrap gap-2 mt-4 sm:mt-5 justify-center">
              {activeView.places.map((place, i) => {
                const isActive = activeId === place.id;
                return (
                  <span
                    key={place.id}
                    onMouseEnter={() => setActiveId(place.id)}
                    onMouseLeave={() => setActiveId(null)}
                    style={{ animationDelay: `${i * 60}ms` }}
                    className={`inline-flex items-center gap-2 font-label text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-all duration-300 animate-chip-cascade ${
                      isActive
                        ? "bg-white/12 border-white/25 text-white"
                        : "bg-white/[0.04] border-white/10 text-foreground-200 hover:bg-white/[0.08] hover:border-white/20"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${s.chipDot}`} />
                    <span className="font-semibold">{t(place.cityKey)}</span>
                    <span className="text-foreground-400/60">{t(place.countryKey)}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}